// Sample datasets for Spectra — one per analysis type

export interface ExampleDataset {
  id: string;
  name: string;
  description: string;
  category: string;
  headers: string[];
  rows: (string | number)[][];
}

export const EXAMPLE_DATASETS: ExampleDataset[] = [
  {
    id: 'dose_response',
    name: 'EGFR Inhibitor IC50',
    description: 'Dose-response data for an EGFR kinase inhibitor (10 concentrations, triplicates)',
    category: 'curve_fitting',
    headers: ['Concentration (nM)', 'Replicate 1', 'Replicate 2', 'Replicate 3'],
    rows: [
      [0.1, 98.2, 97.5, 99.1],
      [0.3, 96.8, 95.2, 97.3],
      [1, 92.1, 90.5, 93.4],
      [3, 82.4, 80.1, 84.6],
      [10, 61.3, 58.9, 63.7],
      [30, 35.2, 32.8, 37.1],
      [100, 15.6, 13.2, 17.8],
      [300, 6.8, 5.1, 8.2],
      [1000, 3.2, 2.5, 4.1],
      [3000, 2.1, 1.8, 2.6],
    ],
  },
  {
    id: 'ttest_viability',
    name: 'Cell Viability — Control vs Treated',
    description: 'Cell viability (%) in control vs drug-treated groups (n=10 each)',
    category: 'compare_groups',
    headers: ['Control', 'Treated'],
    rows: [
      [95.2, 72.1],
      [98.1, 68.5],
      [92.4, 75.3],
      [96.7, 70.8],
      [94.3, 66.2],
      [97.8, 73.9],
      [93.5, 69.4],
      [99.2, 71.7],
      [95.6, 67.8],
      [96.1, 74.2],
    ],
  },
  {
    id: 'enzyme_kinetics',
    name: 'Trypsin Kinetics',
    description: 'Substrate concentration vs reaction velocity for trypsin (Michaelis-Menten)',
    category: 'curve_fitting',
    headers: ['[S] (mM)', 'Velocity (nmol/min)'],
    rows: [
      [0.1, 12.5],
      [0.2, 22.1],
      [0.5, 42.8],
      [1.0, 61.3],
      [2.0, 78.9],
      [5.0, 92.4],
      [10.0, 98.7],
      [20.0, 102.1],
      [50.0, 105.3],
      [100.0, 106.8],
    ],
  },
  {
    id: 'spr_binding',
    name: 'SPR Antibody Binding',
    description: 'Surface plasmon resonance sensorgram for antibody-antigen binding',
    category: 'spr_kinetics',
    headers: ['Time (s)', 'Response (RU)', 'Concentration (nM)'],
    rows: [
      [0, 0.5, 10],
      [10, 15.2, 10],
      [20, 28.7, 10],
      [30, 38.4, 10],
      [60, 52.1, 10],
      [90, 56.8, 10],
      [120, 58.2, 10],
      [150, 55.1, 10],
      [180, 48.3, 10],
      [210, 40.2, 10],
      [240, 33.5, 10],
      [270, 27.8, 10],
      [300, 23.1, 10],
    ],
  },
  {
    id: 'correlation_mw_logp',
    name: 'MW vs LogP Correlation',
    description: 'Molecular weight vs LogP for 20 drug candidates',
    category: 'correlation',
    headers: ['MW', 'LogP'],
    rows: [
      [180.2, 1.2],
      [210.5, 1.8],
      [245.3, 2.1],
      [198.7, 1.5],
      [312.4, 2.8],
      [278.6, 2.4],
      [156.2, 0.8],
      [342.5, 3.1],
      [289.1, 2.6],
      [225.8, 1.9],
      [367.2, 3.5],
      [195.4, 1.3],
      [410.6, 3.8],
      [265.9, 2.2],
      [320.1, 2.9],
      [185.3, 1.1],
      [295.7, 2.5],
      [350.8, 3.3],
      [230.4, 2.0],
      [275.2, 2.3],
    ],
  },
  {
    id: 'anova_treatments',
    name: 'Three Treatment Groups',
    description: 'Tumour volume (mm\u00B3) across three treatment arms (n=8 each)',
    category: 'compare_groups',
    headers: ['Vehicle', 'Low Dose', 'High Dose'],
    rows: [
      [245, 198, 142],
      [267, 185, 128],
      [232, 210, 155],
      [258, 192, 135],
      [271, 178, 148],
      [240, 205, 131],
      [253, 195, 140],
      [262, 188, 138],
    ],
  },
  {
    id: 'descriptive_assay',
    name: 'Assay Reproducibility',
    description: 'IC50 values (nM) from 20 repeat measurements of the same compound',
    category: 'descriptive',
    headers: ['IC50 (nM)'],
    rows: [
      [45.2], [48.1], [43.7], [46.5], [47.8],
      [44.3], [49.2], [45.8], [46.1], [47.3],
      [44.9], [46.7], [48.5], [45.1], [47.0],
      [43.8], [46.2], [48.8], [44.5], [47.6],
    ],
  },
];

export function getExamplesForCategory(category: string): ExampleDataset[] {
  return EXAMPLE_DATASETS.filter(d => d.category === category);
}

export function getExampleById(id: string): ExampleDataset | undefined {
  return EXAMPLE_DATASETS.find(d => d.id === id);
}
